import React, { useEffect, useState } from 'react';
import DrawerComponent from '../../../ui/Drawer';
import LabelText from '../../../ui/LabelText';
import { DrawerContainer, DrawerHeader, SearchResults } from '../../../styled-components/Drawer.Styles';
import { ForawrdBackwardButton } from '../../../styled-components/PageHeaderStyles';
import CloseIcon from '@mui/icons-material/Close';
import { CheckboxInput } from '../../../ui/FilterDropdown';
import NumberTextField from '../../../ui/NumberTextField';
import PrimaryButton from '../../../ui/PrimaryButton';
import { useSearch } from '../../../contexts/PlayerSearchContext';
import { attributeSections } from '../../../data/attributeSection';
import { AttributesList } from '../../../styled-components/PlayerComponent.styles';
import { usePlayerSearch } from '../../../contexts/SearchPlayersContext';

interface ISelectAttributeProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (selectedData: IAttribute[]) => void;
    selectedAttributes: IAttribute[];
}

export interface IAttribute {
    id: string;
    label: string;
    displayName: string;
    checked: boolean;
    percentile: number;
    isMadeUpScore: boolean;
}
export interface ISection {
    title: string;
    attributes: IAttribute[];
}


const SelectAttributes: React.FC<ISelectAttributeProps> = ({ isOpen, onClose, onConfirm, selectedAttributes }) => {
    const { playerInitialAttributes } = usePlayerSearch();
    const [sectionsState, setSectionsState] = useState<ISection[]>(attributeSections);
    useEffect(() => {
        const updatedSections = attributeSections.map(section => ({
            ...section,
            attributes: section.attributes.map(attr => {
                const selectedAttr = selectedAttributes.find(selected => selected.id === attr.id);
                return selectedAttr ? { ...attr, checked: selectedAttr.checked, percentile: selectedAttr.percentile } : attr;
            })
        }));
        setSectionsState(updatedSections);
    }, [selectedAttributes]);

    const updateSectionsState = (sectionTitle: string, id: string, updates: Partial<IAttribute>) => {
        const newSections = sectionsState.map(section => {
            if (section.title === sectionTitle) {
                return {
                    ...section,
                    attributes: section.attributes.map(attr =>
                        attr.id === id ? { ...attr, ...updates } : attr
                    ),
                };
            }
            return section;
        });
        setSectionsState(newSections);
    };

    const handleCheckboxChange = (sectionTitle: string, id: string) => {
        updateSectionsState(sectionTitle, id, {
            checked: !sectionsState.find(section => section.title === sectionTitle)?.attributes.find(attr => attr.id === id)?.checked,
            isMadeUpScore: playerInitialAttributes.length === 0 ? true : false
        });
    };

    const handlePercentileChange = (sectionTitle: string, id: string, value: number) => {
        updateSectionsState(sectionTitle, id, { percentile: value, isMadeUpScore: playerInitialAttributes.length === 0 ? true : false });
    };

    const handleConfirm = () => {
        if (playerInitialAttributes.length > 0) {
            const updatedSections = sectionsState.map((section, sectionIndex) => {
                const updatedAttributes = section.attributes.map((attr, attrIndex) => {
                    const initialAttr = playerInitialAttributes[sectionIndex].attributes[attrIndex];
                    return {
                        ...attr,
                        isMadeUpScore: attr.percentile !== initialAttr.percentile, // Compare with initial values
                    };
                });
                return { ...section, attributes: updatedAttributes };
            });
            const selectedAttributelist = updatedSections.flatMap(section => section.attributes).filter(attribute => attribute.checked);
            onConfirm(selectedAttributelist);
        }
        else {
            const selectedAttributelist = sectionsState.flatMap(section => section.attributes).filter(attribute => attribute.checked);
            onConfirm(selectedAttributelist);
        }
        onClose();
    }

    const anyAttributeChecked = sectionsState.flatMap(section => section.attributes).some(attribute => attribute.checked);

    return (
        <DrawerComponent
            open={isOpen}
            onClose={onClose}
            styleVariant={'attribute'}
            customClassName='sidebar-drawer-main attribute-drawer'
        >
            <DrawerContainer className='main-drawer-container attribute-drawer'>
                <DrawerHeader className='drawer-header-top'>
                    <LabelText className='drawer-title'>Select attributes</LabelText>
                    <div className='header-buttons'>
                        <ForawrdBackwardButton onClick={onClose}>
                            <CloseIcon className='cta-btn-icons' />
                        </ForawrdBackwardButton>
                    </div>
                </DrawerHeader>
                <div className='select-attributes-content'>
                    {sectionsState.map(section => (
                        <div key={section.title} className='section'>
                            <LabelText className='custom-list-strong-text' style={{ marginBottom: "15px" }}>{section.title}</LabelText>
                            <div className='attributes-list'>
                                {section.attributes.map(attr => (
                                    <AttributesList key={attr.id} checked={attr.checked} className='attribute-item attribute-content'>
                                        <CheckboxInput checked={attr.checked} className='attribute-checkbox-input'>
                                            <input
                                                type="checkbox"
                                                checked={attr.checked}
                                                onChange={() => handleCheckboxChange(section.title, attr.id)}
                                                className="checkbox"
                                            />
                                            <span className={`label-text`}>{attr.label}</span>
                                        </CheckboxInput>
                                        <div className={`percentile-box ${attr.checked ? 'selected-attributes' : 'unselected-attributes'}`}>
                                            <NumberTextField
                                                type="attributes"
                                                id={attr.id}
                                                maxLength={attr.id === 'Height Cm' ? 3 : 5}
                                                value={attr.percentile} 
                                                onChange={(e) => handlePercentileChange(section.title, attr.id, parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                    </AttributesList>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                {anyAttributeChecked && (
                    <SearchResults className='confirm-attribute-btn'>
                        <PrimaryButton onClick={handleConfirm} iconType='confirm' label='Confirm' />
                    </SearchResults>
                )}
            </DrawerContainer>
        </DrawerComponent>
    )
}

export default SelectAttributes;
